import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, EmptyState } from '../components/ui/SharedUI';
import SafeIcon from '../common/SafeIcon';
import { demoRepository } from '../repository/demoRepository';
import { calculations } from '../utils/calculations';

const normaliseStatus = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');

const toTimestamp = (value) => {
  if (!value) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const getUrgency = (days) => {
  if (days < 0) {
    return {
      ring: '#dc2626',
      text: 'text-red-700',
      soft: 'bg-red-50',
      border: 'border-red-200',
      badge: 'bg-red-50 text-red-700 border-red-200'
    };
  }
  if (days <= 7) {
    return {
      ring: '#ef4444',
      text: 'text-red-700',
      soft: 'bg-red-50',
      border: 'border-red-200',
      badge: 'bg-red-50 text-red-700 border-red-200'
    };
  }
  if (days <= 30) {
    return {
      ring: '#f59e0b',
      text: 'text-amber-700',
      soft: 'bg-amber-50',
      border: 'border-amber-200',
      badge: 'bg-amber-50 text-amber-700 border-amber-200'
    };
  }
  if (days <= 90) {
    return {
      ring: '#2563eb',
      text: 'text-blue-700',
      soft: 'bg-blue-50',
      border: 'border-blue-200',
      badge: 'bg-blue-50 text-blue-700 border-blue-200'
    };
  }
  return {
    ring: '#059669',
    text: 'text-emerald-700',
    soft: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  };
};

const sortNearestFirst = (a, b) => {
  const aOverdue = a.days < 0;
  const bOverdue = b.days < 0;
  if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
  if (aOverdue) return Math.abs(a.days) - Math.abs(b.days);
  if (a.days !== b.days) return a.days - b.days;
  return String(a.title || '').localeCompare(String(b.title || ''), 'en-AU', { sensitivity: 'base' });
};

const formatRentBasis = (value) => {
  const key = normaliseStatus(value);
  if (key === 'fortnightly') return 'fortnight';
  if (key === 'monthly') return 'month';
  if (key === 'quarterly') return 'quarter';
  if (key === 'annual' || key === 'annually') return 'year';
  return 'week';
};

const CountdownRing = ({ days, startDate, eventDate }) => {
  const [animate, setAnimate] = useState(false);
  const urgency = getUrgency(days);
  const start = toTimestamp(startDate);
  const end = toTimestamp(eventDate);
  const now = Date.now();
  const total = start && end && end > start ? end - start : null;
  const elapsed = total ? Math.min(total, Math.max(0, now - start)) : null;
  const progress = days < 0 ? 100 : total ? Math.min(100, Math.max(4, (elapsed / total) * 100)) : 72;
  const radius = 39;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - (animate ? progress : 0) / 100);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setAnimate(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="relative h-24 w-24 sm:h-28 sm:w-28 flex-shrink-0" aria-label={days < 0 ? `${Math.abs(days)} days overdue` : `${days} days remaining`}>
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90" aria-hidden="true">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="8" />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={urgency.ring}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 850ms cubic-bezier(0.4,0,0.2,1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className={`text-2xl sm:text-3xl font-bold leading-none ${urgency.text}`}>{Math.abs(days)}</div>
        <div className={`mt-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-wide ${urgency.text}`}>
          {days < 0 ? 'overdue' : days === 1 ? 'day' : 'days'}
        </div>
      </div>
    </div>
  );
};

const LeaseDateCard = ({ event, onOpen }) => {
  const urgency = getUrgency(event.days);
  const isReview = event.type === 'rent_review';

  return (
    <article
      className={`group rounded-2xl border bg-white p-3 sm:p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${urgency.border}`}
    >
      <div className="grid grid-cols-[82px_minmax(0,1fr)] gap-3 sm:grid-cols-[100px_minmax(0,1fr)_auto] sm:items-center">
        <div className={`flex min-h-[92px] flex-col items-center justify-center rounded-xl border-l-4 ${urgency.soft} ${urgency.border}`}>
          <div className={`text-3xl font-bold leading-none ${urgency.text}`}>{Math.abs(event.days)}</div>
          <div className={`mt-1 text-[10px] font-bold uppercase tracking-wide ${urgency.text}`}>
            {event.days < 0 ? 'days overdue' : event.days === 1 ? 'day' : 'days'}
          </div>
        </div>

        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${urgency.badge}`}>
              <SafeIcon icon={isReview ? 'TrendingUp' : 'FileText'} className="text-xs" />
              {isReview ? 'Rent Review' : 'Lease Expiry'}
            </span>
            {event.days < 0 && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-red-600">Action required</span>
            )}
          </div>
          <h3 className="truncate text-sm font-bold text-slate-900 sm:text-base">{event.propertyName}</h3>
          <div className="mt-2 grid gap-1 text-xs text-slate-600 sm:grid-cols-2 sm:text-sm">
            <div className="flex min-w-0 items-center gap-2">
              <SafeIcon icon="User" className="flex-shrink-0 text-slate-400" />
              <span className="truncate">{event.tenantName}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <SafeIcon icon="Home" className="flex-shrink-0 text-slate-400" />
              <span className="truncate">{event.spaceName}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2 font-semibold text-slate-800">
              <SafeIcon icon="DollarSign" className="flex-shrink-0 text-slate-400" />
              <span>{event.rentLabel}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <SafeIcon icon="Calendar" className="flex-shrink-0 text-slate-400" />
              <span>{isReview ? 'Review due' : 'Lease ends'} {calculations.formatDateAU(event.date)}</span>
            </div>
          </div>
        </div>

        <div className="col-span-2 flex justify-end sm:col-span-1">
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex min-h-[40px] items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 shadow-sm transition-colors hover:bg-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
          >
            {isReview ? 'Review Rent' : 'Open Lease'}
            <SafeIcon icon="ArrowRight" />
          </button>
        </div>
      </div>
    </article>
  );
};

const InsuranceDateCard = ({ policy, onOpen, expanded = false }) => {
  const urgency = getUrgency(policy.days);
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`group w-full rounded-2xl border bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${urgency.border}`}
    >
      <div className={`flex items-center gap-4 ${expanded ? 'sm:gap-6' : ''}`}>
        <CountdownRing days={policy.days} startDate={policy.startDate} eventDate={policy.date} />
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${urgency.badge}`}>
              <SafeIcon icon="Shield" className="text-xs" />
              {policy.typeLabel}
            </span>
          </div>
          <h3 className="truncate text-sm font-bold text-slate-900 sm:text-base">{policy.propertyName}</h3>
          <div className="mt-2 space-y-1 text-xs text-slate-600 sm:text-sm">
            <div className="flex items-center gap-2"><SafeIcon icon="Shield" className="text-slate-400" /><span className="truncate">{policy.provider}</span></div>
            {policy.policyNumber && <div className="flex items-center gap-2"><SafeIcon icon="FileText" className="text-slate-400" /><span className="truncate">{policy.policyNumber}</span></div>}
            <div className="flex items-center gap-2 font-semibold text-slate-800"><SafeIcon icon="DollarSign" className="text-slate-400" /><span>{calculations.formatCurrencyAUD(policy.premium)}/year</span></div>
            <div className="flex items-center gap-2"><SafeIcon icon="Calendar" className="text-slate-400" /><span>Renews {calculations.formatDateAU(policy.date)}</span></div>
          </div>
        </div>
        <SafeIcon icon="ChevronRight" className="flex-shrink-0 text-xl text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-amber-600" />
      </div>
    </button>
  );
};

const ImportantDatesPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const handleRefresh = () => setRefreshTrigger(value => value + 1);
    window.addEventListener('mpp:data-refreshed', handleRefresh);
    return () => window.removeEventListener('mpp:data-refreshed', handleRefresh);
  }, []);

  const data = useMemo(() => {
    const properties = demoRepository.getAllProperties();
    const tenants = demoRepository.getAllTenants();
    const spaces = demoRepository.getAllSpaces ? demoRepository.getAllSpaces() : [];
    const leases = demoRepository.getAllLeases ? demoRepository.getAllLeases() : [];
    const policies = demoRepository.getAllInsurancePolicies ? demoRepository.getAllInsurancePolicies() : [];

    const propertyMap = new Map(properties.map(item => [item.id, item]));
    const tenantMap = new Map(tenants.map(item => [item.id, item]));
    const spaceMap = new Map(spaces.map(item => [item.id, item]));

    const leaseEvents = [];
    leases.forEach(lease => {
      if (!demoRepository.isContractualLease(lease)) return;

      const property = propertyMap.get(lease.propertyId);
      const tenant = tenantMap.get(lease.tenantId);
      const space = spaceMap.get(lease.spaceId);
      const propertyName = property?.name || 'Property';
      const tenantName = tenant ? [tenant.firstName, tenant.lastName].filter(Boolean).join(' ') || tenant.name || 'Tenant' : 'Tenant not assigned';
      const spaceName = space?.name || 'Whole property';
      const rentLabel = `${calculations.formatCurrencyAUD(Number(lease.weeklyRent || lease.rentAmount || 0))}/${formatRentBasis(lease.rentBasis)}`;

      const endDate = toTimestamp(lease.endDate ?? lease.leaseEnd);
      if (endDate) {
        leaseEvents.push({
          id: `lease-expiry-${lease.id}`,
          leaseId: lease.id,
          tenantId: lease.tenantId,
          type: 'lease_expiry',
          title: 'Lease Expiry',
          date: endDate,
          days: calculations.calculateDaysToExpiry(endDate),
          propertyName,
          tenantName,
          spaceName,
          rentLabel
        });
      }

      const reviewDate = toTimestamp(lease.rentReviewDate);
      const reviewType = normaliseStatus(lease.rentReviewTypeValue || lease.rentReviewType);
      if (reviewDate && reviewType !== 'none' && (!endDate || reviewDate <= endDate)) {
        leaseEvents.push({
          id: `rent-review-${lease.id}`,
          leaseId: lease.id,
          tenantId: lease.tenantId,
          type: 'rent_review',
          title: 'Rent Review',
          date: reviewDate,
          days: calculations.calculateDaysToExpiry(reviewDate),
          propertyName,
          tenantName,
          spaceName,
          rentLabel
        });
      }
    });

    const typeLabels = {
      building: 'Building Insurance',
      landlord: 'Landlord Insurance',
      contents: 'Contents Insurance',
      strata: 'Strata Insurance',
      public_liability: 'Public Liability',
      other: 'Other Insurance'
    };

    const insuranceEvents = policies
      .filter(policy => ['active', 'renewal_due', 'claim_open'].includes(normaliseStatus(policy.status)))
      .map(policy => {
        const expiry = toTimestamp(policy.expiryDate);
        if (!expiry) return null;
        const property = propertyMap.get(policy.propertyId);
        return {
          id: policy.id,
          title: typeLabels[normaliseStatus(policy.policyType)] || 'Insurance',
          typeLabel: typeLabels[normaliseStatus(policy.policyType)] || 'Insurance',
          date: expiry,
          days: calculations.calculateDaysToExpiry(expiry),
          startDate: toTimestamp(policy.startDate),
          propertyName: property?.name || 'Property',
          provider: policy.provider || 'Provider not recorded',
          policyNumber: policy.policyNumber || '',
          premium: Number(policy.premium || 0)
        };
      })
      .filter(Boolean);

    return {
      leaseEvents: leaseEvents.sort(sortNearestFirst),
      insuranceEvents: insuranceEvents.sort(sortNearestFirst)
    };
  }, [refreshTrigger]);

  const allCount = data.leaseEvents.length + data.insuranceEvents.length;
  const tabs = [
    { id: 'all', label: 'All', icon: 'Calendar', count: allCount },
    { id: 'leases', label: 'Leases', icon: 'FileText', count: data.leaseEvents.length },
    { id: 'insurance', label: 'Insurance', icon: 'Shield', count: data.insuranceEvents.length }
  ];

  const openLease = (event) => {
    if (event.tenantId) navigate(`/tenants/${event.tenantId}`);
    else navigate('/tenants');
  };

  const openInsurance = (policy) => {
    navigate(`/insurance?policy=${encodeURIComponent(policy.id)}`);
  };

  const showLeases = activeTab !== 'insurance';
  const showInsurance = activeTab !== 'leases';

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Important Dates"
        subtitle="Stay on top of lease expiries, rent reviews and insurance renewals. Automatically sorted by nearest date first."
        backAction={() => navigate('/')}
      />

      <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
          {tabs.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex min-h-[42px] flex-shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 ${active ? 'border-amber-400 bg-amber-400 text-slate-950' : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'}`}
              >
                <SafeIcon icon={tab.icon} />
                {tab.label}
                <span className={`rounded-full px-2 py-0.5 text-xs ${active ? 'bg-white/50 text-slate-900' : 'bg-white text-slate-500'}`}>{tab.count}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-start gap-2 rounded-xl bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-800 sm:max-w-md">
          <SafeIcon icon="Info" className="mt-0.5 flex-shrink-0 text-blue-600" />
          <span>These counters are derived live from your existing lease and insurance dates. No duplicate reminder data is stored.</span>
        </div>
      </div>

      <div className={`grid grid-cols-1 gap-5 ${activeTab === 'all' ? 'xl:grid-cols-[minmax(0,1.45fr)_minmax(360px,0.8fr)]' : ''}`}>
        {showLeases && (
          <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3 shadow-sm sm:p-5" aria-labelledby="lease-important-dates">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white text-slate-800 shadow-sm ring-1 ring-slate-200">
                  <SafeIcon icon="FileText" className="text-xl" />
                </div>
                <div>
                  <h2 id="lease-important-dates" className="text-lg font-bold text-slate-900">Lease-related Dates</h2>
                  <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">Lease expiries and rent reviews for active leases.</p>
                </div>
              </div>
              <span className="flex-shrink-0 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">{data.leaseEvents.length} important</span>
            </div>

            {data.leaseEvents.length > 0 ? (
              <div className="space-y-3">
                {data.leaseEvents.map(event => (
                  <LeaseDateCard key={event.id} event={event} onOpen={() => openLease(event)} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="Calendar"
                title="No Active Lease Dates"
                description="Active lease expiries and rent reviews will appear here automatically when they are recorded."
              />
            )}
          </section>
        )}

        {showInsurance && (
          <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3 shadow-sm sm:p-5" aria-labelledby="insurance-important-dates">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white text-slate-800 shadow-sm ring-1 ring-slate-200">
                  <SafeIcon icon="Shield" className="text-xl" />
                </div>
                <div>
                  <h2 id="insurance-important-dates" className="text-lg font-bold text-slate-900">Upcoming Insurance Renewals</h2>
                  <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">Current insurance policies linked to your properties.</p>
                </div>
              </div>
              <span className="flex-shrink-0 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">{data.insuranceEvents.length} policies</span>
            </div>

            {data.insuranceEvents.length > 0 ? (
              <div className={activeTab === 'insurance' ? 'grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3' : 'space-y-3'}>
                {data.insuranceEvents.map(policy => (
                  <InsuranceDateCard
                    key={policy.id}
                    policy={policy}
                    expanded={activeTab === 'insurance'}
                    onOpen={() => openInsurance(policy)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="Shield"
                title="No Current Insurance Renewals"
                description="Active insurance policies with expiry dates will appear here automatically."
              />
            )}

            <button
              type="button"
              onClick={() => navigate('/insurance')}
              className="mt-4 flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              View all insurance policies
              <SafeIcon icon="ChevronRight" />
            </button>
          </section>
        )}
      </div>

      <div className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-100 bg-amber-50/70 px-4 py-3 text-xs leading-5 text-slate-600 sm:text-sm">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-amber-600 shadow-sm">
          <SafeIcon icon="Info" />
        </div>
        <div>
          <div className="font-semibold text-slate-800">Click any item to open its source record.</div>
          <div className="mt-0.5">Terminated, archived or expired leases are not shown as upcoming lease events. Cancelled, archived and expired insurance policies are also excluded.</div>
        </div>
      </div>
    </div>
  );
};

export default ImportantDatesPage;